import React, { Component, Fragment } from "react";
import {
  Alert,
  Modal,
  ModalHeader,
  ModalFooter,
  ModalBody,
  Container,
  Row,
  Col,
  Button,
  Badge,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import LoadingSpinner from "../Components/Common/LoadingSpinner";
import CharitySelectionModal from "../Components/Common/CharitySelectionModal";
import LoginModal from "../Components/Common/LoginModal";
import ShippingInfoModal from "../Components/Common/ShippingInfoModal";
import OrderSummary from "../Components/Common/OrderSummary";
import MainPage from "../Components/Shared/MainPage";
import {
  GetShoppingCartItem,
  GetCharityInfo,
  PreCheckoutApi,
  RemoveFromCartApi,
  SetPreSelectedCharityIdApi,
  UpdateCartTipApi,
  UpdateCardShippingInfoApi,
  ShoppingCartItemTypes,
  DeliveryMethods,
  AccountTypes,
  PaymentTypes,
  EditUrls,
  ImageUrls,
} from "../Code/Data";
import { scrollToElement } from "../Code/Utilities";

export default class ShoppingCart extends Component {
  constructor(props) {
    super(props);
    this.state = {
      shoppingCartItems: null,
      showCharitySelectionModal: false,
      showCorporateCheckModal: false,
      showLoginModal: false,
      selectedCheckoutType: null,
      preselectedCharity: null,
      hasEcards: false,
      wouldLikeNotifications: true,
      readyForCheckout: false,
      errMsg: null,
      loading: true,
      tipAmount: 0,
      currentShippingModalIndex: -1,
    };

    this.getPreselectedCharity = this.getPreselectedCharity.bind(this);
    this.getEcardReminderEligibility = this.getEcardReminderEligibility.bind(
      this
    );
    this.getCorporateCheckEligibility = this.getCorporateCheckEligibility.bind(
      this
    );
    this.getDerivedState = this.getDerivedState.bind(this);
    this.preCheckOut = this.preCheckOut.bind(this);
    this.handleRemoval = this.handleRemoval.bind(this);
    this.hasFilledOutShippingInfo = this.hasFilledOutShippingInfo.bind(this);
    this.updateTipAmount = this.updateTipAmount.bind(this);
    this.selectCharity = this.selectCharity.bind(this);
    this.saveShippingInfo = this.saveShippingInfo.bind(this);
    this.showShippingModal = this.showShippingModal.bind(this);
  }

  async componentDidMount() {
    console.log("MOUNTING");
    await this.getDerivedState();
  }

  async componentDidUpdate(prevProps) {
    if (
      this.props.shoppingCart !== prevProps.shoppingCart ||
      this.props.currentUser !== prevProps.currentUser
    ) {
      console.log("UPDATING");
      await this.getDerivedState();
    } else {
      console.log("NOT UPDATING");
    }
  }

  async getDerivedState() {
    let { currentUser, shoppingCart } = this.props;

    let [
      preselectedCharity,
      hasEcards,
      canUseCorporateCheck,
    ] = await Promise.all([
      this.getPreselectedCharity(shoppingCart),
      this.getEcardReminderEligibility(shoppingCart),
      this.getCorporateCheckEligibility(currentUser, shoppingCart),
    ]);

    this.setState({
      preselectedCharity,
      hasEcards,
      canUseCorporateCheck,
      loading: false,
    });
  }

  async getPreselectedCharity(shoppingCart) {
    if (!shoppingCart) {
      return null;
    }

    let { PreSelectedCharityId } = shoppingCart;
    if (PreSelectedCharityId > 0) {
      return await GetCharityInfo(shoppingCart.PreSelectedCharityId);
    } else {
      return null;
    }
  }

  async getEcardReminderEligibility(shoppingCart) {
    if (!shoppingCart) {
      return false;
    }
    let digitalItems = shoppingCart.Items.filter(
      item =>
        item.ShoppingCartItemTypeId ===
        ShoppingCartItemTypes.DigitalCharityCards
    );
    console.log("DIGITAL ITEMS", digitalItems);
    let itemsFromApi = await Promise.all(
      digitalItems.map(async item => {
        let response = await GetShoppingCartItem(item.ItemIndex);
        if (response.Succeeded) {
          return response.Donationinfo;
        } else {
          throw response.ErrorMessage;
        }
      })
    );
    let hasEcards = itemsFromApi.reduce(
      (acc, item) =>
        acc || item.DigitalCardDeliveryType === DeliveryMethods.Email,
      false
    );
    return hasEcards;
  }

  /*
  For orders where all the following are true, the user is given the option to pay by corporate check.
    1. There is at least one item in the cart that is NOT an Honor Cards or Direct to Charity Donation.
    2. If the total cost for the order is at least $500 or more
    3. If the donor chose not to show the denominations to the recipient, the total cost of the order must be at least $2500 or more
    4. The user must manually confirm that they are ordering these cards for a corporate account.
  */
  async getCorporateCheckEligibility(currentUser, shoppingCart) {
    if (!currentUser || !shoppingCart) {
      return false;
    }

    const ineligibleItemTypes = [
      ShoppingCartItemTypes.DirectToCharityDonation,
      ShoppingCartItemTypes.HonorCardToEmail,
      ShoppingCartItemTypes.HonorCardToPrint,
    ];
    const hasEligibleItem = shoppingCart.Items.reduce(
      (acc, item) =>
        acc || !ineligibleItemTypes.includes(item.ShoppingCartItemTypeId),
      false
    );
    const largeEnoughOrder = shoppingCart.GrandTotal >= 500;
    const isBusinessAccount =
      currentUser.AccountType === AccountTypes.Corporate;
    return hasEligibleItem && largeEnoughOrder && isBusinessAccount;
  }

  hasFilledOutShippingInfo(shoppingCart) {
    return (
      shoppingCart &&
      shoppingCart.Items &&
      shoppingCart.Items.reduce(
        (acc, item) => acc && item.HasCompleteShippingInfo === "True",
        true
      )
    );
  }

  async preCheckOut(currentUser) {
    let { shoppingCart } = this.props;
    if (!this.hasFilledOutShippingInfo(shoppingCart)) {
      this.setState({
        errMsg: "Please fill out all information before checking out",
      });
      setTimeout(() => scrollToElement(".alert-danger", true, 0), 500);
      return;
    }

    if (!currentUser) {
      this.setState({ showLoginModal: true });
      return;
    }

    let {
      hasEcards,
      wouldLikeNotifications,
      selectedCheckoutType,
    } = this.state;

    let precheckOutRequest = {
      IsPayPalExpress: selectedCheckoutType === PaymentTypes.Paypal,
      IsCorporateCheck: selectedCheckoutType === PaymentTypes.Corporate,
      SendEcardReminder: hasEcards && wouldLikeNotifications,
      CategoryForShowOnly: null,
      CategoryForShowAlso: null,
      CustomCharityListId: null,
      CustomCharityList: null,
      PreSelectedCharityId: shoppingCart.PreSelectedCharityId,
      ExpressCheckoutUrl: `https://${window.location.host}/Purchase/PayPalCheckout`,
      ExpressCancelUrl: `https://${window.location.host}/Purchase/ShoppingCart`,
    };

    console.log("PRECHECKOUT REQUEST", precheckOutRequest);
    let precheckOutResponse = await PreCheckoutApi(precheckOutRequest);
    console.log("PRECHECKOUT RESPONSE", precheckOutResponse);

    if (precheckOutResponse.Succeeded) {
      switch (selectedCheckoutType) {
        case PaymentTypes.Paypal:
          if (precheckOutResponse.RedirectURL) {
            console.log("REDIRECTING TO PAYPAL");
            window.location.href = precheckOutResponse.RedirectURL;
          }
          break;
        case PaymentTypes.CreditCard:
          console.log("REDIRECTING TO CREDIT CHECKOUT");
          window.location.href = `https://${window.location.host}/Purchase/CreditCheckout`;
          break;
        case PaymentTypes.Corporate:
          console.log("REDIRECTING TO CORPORATE CHECK CHECKOUT");
          window.location.href = `https://${window.location.host}/Purchase/CorporateCheckCheckout`;
          break;
        default:
          break;
      }
    } else {
      this.setState({ errMsg: precheckOutResponse.ErrorMessage });
      setTimeout(() => scrollToElement(".alert-danger", true, 0), 500);
    }
  }

  async updateTipAmount() {
    let { tipAmount } = this.state;
    console.log("UPDATING TIP", tipAmount);
    let response = await UpdateCartTipApi(tipAmount);
    if (response.Succeeded) {
      this.setState({ tipAmount: 0 });
      await this.props.refreshShoppingCart();
    } else {
      let errMsg = response.ErrorMessage || response.Message;
      this.setState({ errMsg });
      setTimeout(
        () => errMsg && scrollToElement(".alert-danger", true, 0),
        500
      );
    }
  }

  async handleRemoval(index) {
    let response = await RemoveFromCartApi(index);
    if (response.Succeeded) {
      await this.props.refreshShoppingCart();
    } else {
      let errMsg = response.ErrorMessage || response.Message;
      this.setState({ errMsg });
      setTimeout(
        () => errMsg && scrollToElement(".alert-danger", true, 0),
        500
      );
    }
  }

  async selectCharity(preselectedCharity) {
    console.log("CLOSED WITH SELECTION", preselectedCharity);
    this.setState({ showCharitySelectionModal: false });
    let response = await SetPreSelectedCharityIdApi(
      preselectedCharity ? preselectedCharity.CharityId : 0
    );
    if (response.Succeeded) {
      await this.props.refreshShoppingCart();
    } else {
      let errMsg = response.ErrorMessage || response.Message;
      this.setState({ errMsg });
      setTimeout(
        () => errMsg && scrollToElement(".alert-danger", true, 0),
        500
      );
    }
  }

  async saveShippingInfo(shippingInfo) {
    console.log("SAVING SHIPPING INFO", shippingInfo);
    let { currentShippingModalIndex } = this.state;
    this.setState({ currentShippingModalIndex: -1 });
    let response = await UpdateCardShippingInfoApi({
      index: currentShippingModalIndex,
      shippingInfo,
    });
    if (response.Succeeded) {
      await this.props.refreshShoppingCart();
    } else {
      let errMsg = response.ErrorMessage || response.Message;
      this.setState({ errMsg });
      setTimeout(
        () => errMsg && scrollToElement(".alert-danger", true, 0),
        500
      );
    }
  }

  showShippingModal(item) {
    this.setState({
      currentShippingModalIndex: item.ItemIndex,
    });
  }

  render() {
    console.log("CART RENDERING", this.props, this.state);

    const { currentUser, shoppingCart } = this.props;
    const {
      loading,
      errMsg,
      showCharitySelectionModal,
      showCorporateCheckModal,
      showLoginModal,
      hasEcards,
      preselectedCharity,
      canUseCorporateCheck,
      tipAmount,
      wouldLikeNotifications,
      currentShippingModalIndex,
    } = this.state;

    const currentShippingInfo =
      shoppingCart && currentShippingModalIndex !== -1
        ? shoppingCart.Items[ currentShippingModalIndex ].ShippingInfo
        : null;

    if (loading) {
      return <LoadingSpinner />;
    } else if (!shoppingCart || !shoppingCart.Items.length) {
      return <MainPage title="Your shopping cart is empty" />;
    }

    return (
      <Container className="cart-page">
        <Row>
          <Col md={8} className="left-pane">
            <Container>
              <Row>
                <h2>Cart</h2>
              </Row>
              {errMsg && (
                <Row>
                  <Alert color="danger">{errMsg}</Alert>
                </Row>
              )}
              {shoppingCart.Items.map(item => (
                <Row className="cart-item" key={item.PaidItemId}>
                  <Col md={2}>
                    {item.CardImageUrl ||
                      ImageUrls[ item.ShoppingCartItemTypeId ] ? (
                        <img
                          className="img-fluid"
                          src={
                            item.CardImageUrl ||
                            ImageUrls[ item.ShoppingCartItemTypeId ]
                          }
                          alt={item.Type}
                        />
                      ) : (
                        <div className="cart-image-spacer"></div>
                      )}
                  </Col>
                  <Col md={5}>
                    <h3>{item.Type}</h3>
                    <p dangerouslySetInnerHTML={{ __html: item.ShortDescription }}></p>
                    <p dangerouslySetInnerHTML={{ __html: item.Description }}></p>
                    <p>{item.IsBulkHiddenAmount ? 'Cost per code' :'Denomination'}: ${item.Denomination.toFixed(2)}</p>
                    {item.HasCompleteShippingInfo === "False" && (
                      <Fragment>
                        <Alert color="light" onClick={() => this.showShippingModal(item)}>Needs Shipping Information</Alert>
                        <ul className='very-small'>{item.ShippingIncompleteIssues.map(i => <li>{i}</li>)}</ul>
                        <Button
                          color="primary"
                          onClick={() => this.showShippingModal(item)}>
                          Update Shipping Address
                        </Button>
                      </Fragment>
                    )}
                    {EditUrls[ item.ShoppingCartItemTypeId ] && (
                      <p>
                        <a href={EditUrls[ item.ShoppingCartItemTypeId ](item)}>
                          Edit Order
                        </a>
                      </p>
                    )}
                  </Col>
                  <Col md={5}>
                    <Row>
                      <Col xs={5}>
                        <p>${item.TotalForCards.toFixed(2)}</p>
                      </Col>
                      <Col xs={4}>
                        <Badge className="quantity-badge">
                          {item.Quantity}
                        </Badge>
                      </Col>
                      <Col xs={3}>
                        <Button
                          close
                          color="dark"
                          className="cart-remove-item"
                          onClick={e => this.handleRemoval(item.ItemIndex)}
                        />
                      </Col>
                    </Row>
                  </Col>
                </Row>
              ))}
              {hasEcards && (
                <Row>
                  <label>
                    <input
                      type="checkbox"
                      checked={wouldLikeNotifications}
                      onChange={e =>
                        this.setState({
                          wouldLikeNotifications: e.target.checked,
                        })
                      }
                    />{" "}
                    A reminder email will be sent to recipients after 6 days, if
                    there is no response.
                  </label>
                </Row>
              )}
              <Row>
                <h5>Would you like to pre-select a charity?</h5>
                <p>
                  This will be suggested to your gift-recipient, as one of their
                  "up to three" charity choices.
                </p>
                {preselectedCharity ? (
                  <Fragment>
                    <p>
                      You have chosen:{" "}
                      <strong>{preselectedCharity.CharityName}</strong>{" "}
                      <a href="javascript:void(0)"
                        onClick={() => this.setState({ showCharitySelectionModal: true })
                        }>
                        Change
                      </a>
                      {" | "}
                      <a
                        href="javascript:void(0)"
                        onClick={e => this.selectCharity(null)}>
                        Remove
                      </a>
                    </p>
                  </Fragment>
                ) : (
                    <Button
                      onClick={e =>
                        this.setState({ showCharitySelectionModal: true })
                      }>
                      Choose a Charity
                    </Button>
                  )}

                <CharitySelectionModal
                  isOpen={showCharitySelectionModal}
                  onClose={this.selectCharity}
                  selectedCharity={preselectedCharity}
                />
                <ShippingInfoModal
                  isOpen={currentShippingModalIndex !== -1}
                  shippingInfo={currentShippingInfo}
                  onClose={() =>
                    this.setState({ currentShippingModalIndex: -1 })
                  }
                  saveInfo={this.saveShippingInfo}
                />
              </Row>
            </Container>
            <p className="text-center">
              <a href="/#GiveGiftCards" color="primary" block={true}>
                <u>Continue Shopping</u>
              </a>
            </p>
          </Col>
          <Col md={4} className="right-pane">
            <OrderSummary shoppingCart={shoppingCart} />
            <div>
              <Button
                onClick={() =>
                  this.setState(
                    { selectedCheckoutType: PaymentTypes.CreditCard },
                    () => this.preCheckOut(currentUser)
                  )
                }
                color="primary"
                block={true}>
                Pay by Credit Card
              </Button>
            </div>
            <div>
              <Button
                onClick={() =>
                  this.setState(
                    { selectedCheckoutType: PaymentTypes.Paypal },
                    () => this.preCheckOut(currentUser)
                  )
                }
                color="primary"
                block={true}>
                Pay by PayPal
              </Button>
            </div>
            {canUseCorporateCheck && (
              <div>
                <Button
                  onClick={() =>
                    this.setState({ showCorporateCheckModal: true })
                  }
                  color="primary"
                  block={true}>
                  Pay by Corporate Check
                </Button>
              </div>
            )}
            <p className="text-center">
              <a href="/#GiveGiftCards" className="text-tertiary" block={true}>
                <u>Continue Shopping</u>
              </a>
            </p>
            <div>
              <h4>Donate to Special Kids Fund</h4>
              <p>
                Help our special kids by leaving a tip to facilitate charitable
                giving with your purchase.
              </p>
              <p>
                <strong>Tips for Special Kids!</strong>
              </p>

              <div className="tip-default-amounts">
                <FormGroup check>
                  <Label check>
                    <Input
                      type="radio"
                      name="tipAmount"
                      checked={tipAmount === 3}
                      onChange={e => this.setState({ tipAmount: 3 })}
                    />
                    $3.00
                  </Label>
                </FormGroup>
                <FormGroup check>
                  <Label check>
                    <Input
                      type="radio"
                      name="tipAmount"
                      checked={tipAmount === 5}
                      onChange={e => this.setState({ tipAmount: 5 })}
                    />
                    $5.00
                  </Label>
                </FormGroup>
                <FormGroup check>
                  <Label check>
                    <Input
                      type="radio"
                      name="tipAmount"
                      checked={tipAmount === 10}
                      onChange={e => this.setState({ tipAmount: 10 })}
                    />
                    $10.00
                  </Label>
                </FormGroup>
              </div>
              <div className="custom-tip-wrapper">
                $
                <Input
                  type="number"
                  value={tipAmount}
                  onChange={e => this.setState({ tipAmount: +e.target.value })}
                />
              </div>
              <Button color="primary" onClick={this.updateTipAmount}>
                Donate
              </Button>
            </div>
          </Col>
        </Row>
        <Modal
          className="corporate-check-modal"
          isOpen={showCorporateCheckModal}
          toggle={() => this.setState({ showCorporateCheckModal: false })}>
          <ModalHeader>Corporate Check Payment</ModalHeader>
          <ModalBody>
            <p>
              <strong>PLEASE NOTE:</strong> The "pay by corporate check" option
              is only available to corporate or charity group fundraiser
              accounts.
            </p>
            <p>Check payable to:</p>
            <address>
              Charity Gift Certificates
              <br />
              111 Autumn Rd.
              <br />
              Lakewood, NJ 08701
              <br />
            </address>
            <p>
              By clicking on the continue button below, you are confirming that
              you are ordering these cards for a corporate account.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button
              color="primary"
              onClick={() =>
                this.setState(
                  {
                    showCorporateCheckModal: false,
                    selectedCheckoutType: PaymentTypes.Corporate,
                  },
                  () => this.preCheckOut(currentUser)
                )
              }>
              Continue
            </Button>
            <Button
              color="secondary"
              onClick={() => this.setState({ showCorporateCheckModal: false })}>
              Cancel
            </Button>
          </ModalFooter>
        </Modal>
        <LoginModal
          isOpen={showLoginModal}
          onClose={() =>
            this.setState({ showLoginModal: false, selectedCheckoutType: null })
          }
          onSuccess={user =>
            this.setState({ showLoginModal: false }, () =>
              this.preCheckOut(user)
            )
          }
        />
      </Container>
    );
  }
}
